import { createTheme } from '@mui/material/styles';

const fontSans =
  '"Plus Jakarta Sans", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const theme = createTheme({
  palette: {
    primary: {
      main: '#0f766e',
      dark: '#115e59',
      light: '#2dd4bf',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#0d9488',
      dark: '#0f766e',
      light: '#5eead4',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#fafafa',
      paper: '#FFFFFF',
    },
    success: { main: '#059669', light: '#d1fae5', contrastText: '#fff' },
    error:   { main: '#dc2626', light: '#fee2e2', contrastText: '#fff' },
    warning: { main: '#d97706', light: '#fef3c7', contrastText: '#fff' },
    info:    { main: '#0f766e' },
    text: {
      primary: '#0a0a0a',
      secondary: '#64748b',
      disabled: '#a1a1aa',
    },
  },
  typography: {
    fontFamily: fontSans,
    h1: {
      fontFamily: fontSans,
      fontWeight: 700,
      letterSpacing: '-0.03em',
      lineHeight: 1.12,
      fontSize: 'clamp(2rem, 5vw, 3.25rem)',
    },
    h2: {
      fontFamily: fontSans,
      fontWeight: 700,
      letterSpacing: '-0.025em',
      lineHeight: 1.18,
      fontSize: 'clamp(1.5rem, 3.5vw, 2.25rem)',
    },
    h3: {
      fontFamily: fontSans,
      fontWeight: 700,
      letterSpacing: '-0.02em',
      lineHeight: 1.22,
      fontSize: 'clamp(1.25rem, 2.8vw, 1.875rem)',
    },
    h4: {
      fontFamily: fontSans,
      fontWeight: 600,
      letterSpacing: '-0.015em',
      lineHeight: 1.26,
      fontSize: 'clamp(1.1rem, 2.2vw, 1.5rem)',
    },
    h5: {
      fontFamily: fontSans,
      fontWeight: 600,
      letterSpacing: '-0.01em',
      lineHeight: 1.35,
      fontSize: '1.25rem',
    },
    h6: {
      fontFamily: fontSans,
      fontWeight: 600,
      letterSpacing: '-0.01em',
      lineHeight: 1.4,
      fontSize: '1.05rem',
    },
    body1: { fontWeight: 400, lineHeight: 1.75, letterSpacing: '0.01em', fontSize: '1rem' },
    body2: { fontWeight: 400, lineHeight: 1.65, letterSpacing: '0.01em', fontSize: '0.875rem' },
    subtitle1: { fontWeight: 600, lineHeight: 1.5,  letterSpacing: '-0.01em' },
    subtitle2: { fontWeight: 500, lineHeight: 1.5,  letterSpacing: '0.01em', fontSize: '0.8125rem' },
    caption:   { fontWeight: 500, letterSpacing: '0.04em', lineHeight: 1.45, fontSize: '0.75rem' },
    button:    { fontWeight: 600, textTransform: 'none', letterSpacing: '0.02em' },
    overline:  { fontWeight: 700, letterSpacing: '0.12em', lineHeight: 1.5, fontSize: '0.7rem' },
  },
  shape: { borderRadius: 12 },
  shadows: [
    'none',
    '0 1px 2px rgba(0,0,0,0.05)',
    '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.05)',
    '0 4px 6px rgba(0,0,0,0.07), 0 2px 4px rgba(0,0,0,0.05)',
    '0 10px 15px rgba(0,0,0,0.07), 0 4px 6px rgba(0,0,0,0.04)',
    '0 20px 25px rgba(0,0,0,0.07), 0 10px 10px rgba(0,0,0,0.04)',
    '0 25px 50px rgba(0,0,0,0.10)',
    '0 25px 50px rgba(0,0,0,0.12)',
    '0 25px 50px rgba(0,0,0,0.14)',
    '0 25px 50px rgba(0,0,0,0.16)',
    '0 25px 50px rgba(0,0,0,0.18)',
    '0 25px 50px rgba(0,0,0,0.20)',
    '0 25px 50px rgba(0,0,0,0.22)',
    '0 25px 50px rgba(0,0,0,0.24)',
    '0 25px 50px rgba(0,0,0,0.26)',
    '0 25px 50px rgba(0,0,0,0.28)',
    '0 25px 50px rgba(0,0,0,0.30)',
    '0 25px 50px rgba(0,0,0,0.32)',
    '0 25px 50px rgba(0,0,0,0.34)',
    '0 25px 50px rgba(0,0,0,0.36)',
    '0 25px 50px rgba(0,0,0,0.38)',
    '0 25px 50px rgba(0,0,0,0.40)',
    '0 25px 50px rgba(0,0,0,0.42)',
    '0 25px 50px rgba(0,0,0,0.44)',
    '0 25px 50px rgba(0,0,0,0.46)',
    '0 25px 50px rgba(0,0,0,0.48)',
  ],
  transitions: {
    duration: { shortest: 150, shorter: 200, short: 250, standard: 300, complex: 375 },
    easing: {
      easeInOut: 'cubic-bezier(0.4,0,0.2,1)',
      easeOut:   'cubic-bezier(0,0,0.2,1)',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          fontFamily: fontSans,
          fontOpticalSizing: 'auto',
        },
        body: {
          fontFamily: fontSans,
          fontOpticalSizing: 'auto',
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          textRendering: 'optimizeLegibility',
          backgroundColor: '#fafafa',
        },
        '#root': {
          fontFamily: fontSans,
          fontOpticalSizing: 'auto',
        },
      },
    },
    MuiGridLegacy: {
      styleOverrides: {
        root: { boxSizing: 'border-box' },
        container: { width: '100%', maxWidth: '100%' },
        item: { boxSizing: 'border-box', minWidth: 0 },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: ({ theme }) => ({
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          marginLeft: 'auto',
          marginRight: 'auto',
          paddingLeft: theme.spacing(2),
          paddingRight: theme.spacing(2),
          [theme.breakpoints.up('sm')]: {
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
          },
          [theme.breakpoints.up('lg')]: { maxWidth: 1400 },
        }),
        maxWidthLg: { maxWidth: 1400, width: '100%' },
        maxWidthMd: { maxWidth: 960 },
        maxWidthSm: { maxWidth: 720 },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          transition: 'all 0.2s ease',
          '&:hover': { transform: 'translateY(-1px)' },
          '&:active': { transform: 'translateY(0)' },
        },
        contained: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(15,118,110,0.30)',
          },
        },
        outlined: {
          borderWidth: 1.5,
          '&:hover': { borderWidth: 1.5 },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
          transition: 'all 0.25s ease',
          overflow: 'hidden',
          '&:hover': {
            boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
            borderColor: '#cbd5e1',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          color: '#0f172a',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: 'h1',
          h2: 'h2',
          h3: 'h3',
          h4: 'h4',
          h5: 'h5',
          h6: 'h6',
          subtitle1: 'p',
          subtitle2: 'p',
          body1: 'p',
          body2: 'p',
        },
      },
    },
  },
});

export default theme;
